import React, { useEffect } from "react";
import { NavLink } from "react-router";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";

export interface SidebarItem {
    to: string;
    icon: IconDefinition;
    label: string;
    end?: boolean;
}

interface Props {
    items: SidebarItem[];
    isOpen: boolean;
    onClose: () => void;
}

const BASE_ITEM = "rounded-lg text-black no-underline! transition-colors hover:bg-surface-3";
const RAIL_ITEM = `${BASE_ITEM} flex w-16 flex-col items-center gap-1 pt-4 pb-[14px] text-center text-[10px] leading-tight`;
const EXPANDED_ITEM = `${BASE_ITEM} flex items-center gap-4 px-3 py-2.5 text-sm`;

export default function Sidebar({ items, isOpen, onClose }: Props) {
    useEffect(() => {
        if (!isOpen) return undefined;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [isOpen, onClose]);

    return (
        <>
            <button
                type="button"
                aria-label="close menu"
                aria-hidden={!isOpen}
                tabIndex={isOpen ? 0 : -1}
                onClick={onClose}
                className={[
                    "fixed inset-0 z-20 cursor-default bg-black/30 lg:hidden",
                    isOpen ? "opacity-100" : "pointer-events-none opacity-0",
                ].join(" ")}
            />
            <nav
                aria-label="primary"
                className={[
                    "fixed bottom-0 left-0 top-14 z-30 flex shrink-0 flex-col overflow-y-auto bg-surface lg:z-10",
                    isOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0",
                    isOpen ? "w-60 gap-1 px-3 py-2" : "w-[72px] px-1",
                    isOpen ? "shadow-xl lg:shadow-none" : "",
                ].join(" ")}
            >
                {items.map((item) => (
                    <NavLink
                        key={item.to}
                        to={item.to}
                        end={item.end}
                        title={item.label}
                        onClick={onClose}
                        className={({ isActive }) =>
                            [
                                isOpen ? EXPANDED_ITEM : RAIL_ITEM,
                                isActive ? "bg-surface-3 font-medium" : "",
                            ].join(" ")
                        }
                    >
                        <FontAwesomeIcon icon={item.icon} className="w-6 shrink-0 text-xl" />
                        <span className={isOpen ? "truncate" : "max-w-full truncate"}>
                            {item.label}
                        </span>
                    </NavLink>
                ))}
            </nav>
        </>
    );
}
